/**
 * Revision types aligned with backend API
 *
 * These types follow the MATIC pattern:
 * - Backend response types use snake_case (e.g., RevisionResponse)
 * - Frontend types use camelCase (e.g., Revision)
 * - Transformation happens in API client convert methods
 */

// ============================================================================
// Backend Response Types (snake_case)
// ============================================================================

/**
 * Content snapshot structure for document resource type
 * Note: This structure varies by resource_type. Currently only "document" is supported.
 */
export type DocumentContentSnapshot = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  file_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  content_type: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  file_size: number;
  metadata?: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  field_values?: Record<string, unknown>;
};

/**
 * Content snapshot structure for training program resource type
 */
export type TrainingProgramContentSnapshot = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_by?: string;
};

/**
 * Union type for all content snapshot response types
 */
export type ContentSnapshotResponse =
  | DocumentContentSnapshot
  | TrainingProgramContentSnapshot;

/**
 * Revision response from backend API
 * Source: GET /api/v1/vault/resources/{resource_id}/revisions
 */
export type RevisionResponse = {
  /** Unique identifier for the revision (UUID) */
  id: string;
  /** Type of resource ("document" | "training" | "procedure" - future) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  resource_type: string;
  /** ID of the parent resource (document_id, etc.) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  resource_id: string;
  /** Numeric revision number (1, 2, 3, ...) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  revision_number: number;
  /** Display label for revision (e.g., "A", "B", "01", "002") */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  revision_label: string;
  /** Snapshot of resource content at this revision */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  content_snapshot: DocumentContentSnapshot;
  /** Description of changes made in this revision */
  changelog: string;
  /** Current status of the revision */
  status: string;
  /** ID of user who created this revision (UUID) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_by: string;
  /** When the revision was created (ISO 8601) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_at: string;
  /** When the revision was last updated (ISO 8601) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  updated_at: string;
};

/**
 * Revision list response from backend API
 */
export type RevisionListResponse = {
  /** List of revisions */
  items: RevisionResponse[];
  /** Total number of revisions matching the query */
  total: number;
  /** Maximum number of revisions returned */
  limit: number;
  /** Number of revisions skipped */
  offset: number;
};

/**
 * Request data for updating a revision (changelog only)
 */
export type UpdateRevisionRequest = {
  /** Updated changelog text */
  changelog: string;
  /** Content snapshot for the revision */
  contentSnapshot: Record<string, any>;
};

/**
 * Request data for publishing a revision
 * No body needed - just POST to publish endpoint
 */
export type PublishRevisionRequest = Record<string, never>;

// ============================================================================
// Frontend Types (camelCase)
// ============================================================================

/**
 * PDF conversion status for files that were converted from DOCX
 */
export type PdfConversionStatus = 'pending' | 'completed' | 'failed';

/**
 * File metadata that includes PDF conversion information
 */
export type FileMetadata = {
  /** Any additional metadata */
  [key: string]: unknown;
  /** ID of the converted PDF file (if conversion completed) */
  pdfFileId?: string;
  /** Status of PDF conversion */
  pdfConversionStatus?: PdfConversionStatus;
  /** Error message if conversion failed */
  pdfConversionError?: string;
  /** ID of the original file (for PDF files created from conversion) */
  convertedFromId?: string;
  /** Content type of the original file */
  convertedFromType?: string;
} & Record<string, unknown>;

/**
 * Content snapshot for frontend (camelCase)
 * Fields are optional to support different resource types (documents, training programs, etc.)
 */
export type ContentSnapshot = {
  name: string;
  fileId?: string;
  contentType?: string;
  fileSize?: number;
  createdBy?: string;
  metadata?: FileMetadata;
  fieldValues?: Record<string, string | string[]>;
};

/**
 * Frontend revision type (transformed from RevisionResponse)
 * All fields in camelCase following MATIC frontend conventions
 */
export type Revision = {
  /** Unique identifier for the revision (UUID) */
  id: string;
  /** Type of resource */
  resourceType: string;
  /** ID of the parent resource (documentId, etc.) */
  resourceId: string;
  /** Numeric revision number (1, 2, 3) */
  revisionNumber: number;
  /** Display label for revision ("RevA", "RevB", "RevC") */
  revisionLabel: string;
  /** Snapshot of resource content at this revision */
  contentSnapshot: ContentSnapshot;
  /** Description of changes made in this revision */
  changelog: string;
  /** Current status of the revision */
  status: RevisionStatus;
  /** ID of user who created this revision */
  createdBy: string;
  /** When the revision was created (ISO 8601) */
  createdAt: string;
  /** When the revision was last updated (ISO 8601) */
  updatedAt: string;

  // Derived fields (frontend only)
  /** Is this the latest/current revision? */
  isCurrent: boolean;
  /** Is this the published revision? */
  isPublished: boolean;
};

/**
 * Revision list type (transformed from RevisionListResponse)
 */
export type RevisionList = {
  /** List of revisions (camelCase) */
  items: Revision[];
  /** Total number of revisions matching the query */
  total: number;
};

// ============================================================================
// Enums and Constants
// ============================================================================

/**
 * Revision status enum
 * Must match backend RevisionStatus exactly
 */
export enum RevisionStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  RETIRED = 'retired',
}

/**
 * Revision action types for permission checking
 */
export enum RevisionAction {
  VIEW = 'view',
  DOWNLOAD = 'download',
  EDIT = 'edit',
  CREATE = 'create',
  PUBLISH = 'publish',
  ARCHIVE = 'archive',
  REVOKE = 'revoke',
}

/**
 * Status badge colors for UI
 */
export const STATUS_COLORS: Record<RevisionStatus, string> = {
  [RevisionStatus.DRAFT]: 'yellow',
  [RevisionStatus.IN_REVIEW]: 'blue',
  [RevisionStatus.APPROVED]: 'blue',
  [RevisionStatus.PUBLISHED]: 'green',
  [RevisionStatus.ARCHIVED]: 'gray',
  [RevisionStatus.RETIRED]: 'red',
};

/**
 * Status display labels for UI
 */
export const STATUS_LABELS: Record<RevisionStatus, string> = {
  [RevisionStatus.DRAFT]: 'Draft',
  [RevisionStatus.IN_REVIEW]: 'In Review',
  [RevisionStatus.APPROVED]: 'Approved',
  [RevisionStatus.PUBLISHED]: 'Published',
  [RevisionStatus.ARCHIVED]: 'Archived',
  [RevisionStatus.RETIRED]: 'Retired',
};
